import React from "react";
import Markdown from "markdown-to-jsx";
import { ExportableTable } from "./ExportableTable";
import { ResultTable } from "./ResultTable";
import { isSafeExportHref } from "./tableExport";
import type { ChatMessage } from "./types";

function MarkdownAnchor({
  href,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  if (!isSafeExportHref(href)) return <span {...rest}>{children}</span>;
  return /^https?:\/\//i.test(href as string) ? (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  ) : (
    <a href={href} {...rest}>
      {children}
    </a>
  );
}

export function MessageContent({
  message,
  tenantId,
  chatBotName,
}: {
  message: ChatMessage;
  tenantId?: string;
  chatBotName?: string;
}) {
  const tables = message.tables || [];
  const placed = new Set<string>();
  const renderTable = (table: typeof tables[number]) => (
    <ResultTable
      key={table.id}
      table={table}
      tenantId={tenantId}
      messageId={message.server_message_id}
      chatBotName={chatBotName}
    />
  );
  // The server includes a Markdown preview for older open clients. Replace
  // only explicitly bound blocks; unrelated Markdown tables remain local.
  const content = (message.content || "").replace(
    /<!-- agentic-result-table:([\w-]+) -->[\s\S]*?<!-- \/agentic-result-table:\1 -->/g,
    (fallback, id: string) =>
      tables.some((table) => table.id === id) ? `[[table:${id}]]` : fallback
  );
  const parts = content.split(/(\[\[table:[^\]\n]+\]\])/g);
  return (
    <>
      {parts.map((part, index) => {
        const token = /^\[\[table:([^\]\n]+)\]\]$/.exec(part);
        if (token) {
          const table = tables.find((item) => item.id === token[1]);
          if (!table || placed.has(table.id)) return null;
          placed.add(table.id);
          return renderTable(table);
        }
        if (!part.trim()) return null;
        return (
          <Markdown
            key={`text-${index}`}
            options={{
              overrides: {
                a: { component: MarkdownAnchor },
                table: {
                  component: ExportableTable,
                  props: { chatBotName },
                },
              },
            }}
          >
            {part}
          </Markdown>
        );
      })}
      {tables.filter((table) => !placed.has(table.id)).map(renderTable)}
    </>
  );
}
